import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "../lib/config";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-[color:var(--owt-bg)]/80 backdrop-blur-xl border-b border-owt-border" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Once Way Travel" className="h-11 md:h-12 w-11 md:w-12 rounded-full object-cover" style={{ objectPosition: "center", transform: "scale(1.35)" }} />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display text-owt-forest text-lg font-bold">Once Way Travel</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  pathname === l.to ? "text-owt-terracotta" : "text-owt-forest hover:text-owt-terracotta"
                }`}
              >
                <span className="text-owt-muted mr-1">0{NAV_LINKS.indexOf(l) + 1}</span>
                {l.label}
              </Link>
            ))}
          </nav>

          <Link
            to="/trips"
            data-testid="nav-cta"
            className="hidden lg:inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 bg-owt-forest text-owt-surface hover:bg-owt-terracotta transition-colors"
          >
            Explore Trips <span aria-hidden>→</span>
          </Link>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 lg:hidden bg-owt-bg pt-24 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                >
                  <Link
                    to={l.to}
                    className="font-display text-5xl text-owt-forest hover:text-owt-terracotta"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    <span className="font-mono text-sm text-owt-muted mr-3">0{i + 1}</span>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
